import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import AgendaCalendarCore from "./AgendaCalendarCore";
import AgendaEventDialog from "./AgendaEventDialog";
import AgendaLegend from "./AgendaLegend";
import AgendaCalendarFilter from "./AgendaCalendarFilter";
import AgendaAvailabilityPanel from "./AgendaAvailabilityPanel";
import AgendaGoogleConnectPrompt from "./AgendaGoogleConnectPrompt";
import AgendaGoogleSyncButton from "./AgendaGoogleSyncButton";
import { useUserAgenda, useUserAgendaMutations } from "../../hooks/useUserAgenda";
import { useAgendaGoogleAutoSync } from "../../hooks/useAgendaGoogleAutoSync";
import { useActiveVideoRooms } from "../../hooks/useActiveVideoRooms";
import { parseManualAgendaEventId } from "../../utils/activeVideoUi";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { teamAgendaPaths } from "../../utils/teamNavigation";
import { chatPath, teamTasksPaths } from "../../utils/teamNavigation";
import {
  AGENDA_SOURCE_KEYS,
  getAgendaEventColors,
  isAgendaEventVisible,
  mergeAgendaFeedEvent,
} from "./agendaUtils";

dayjs.locale("pt-br");

function defaultNewEventRange() {
  const start = new Date();
  const end = dayjs(start).add(1, "hour").toDate();
  return { start, end, allDay: false };
}

export default function TeamAgendaPanel({
  containerStyle,
  isMobile,
  onClose,
  basePath = "/equipe",
  fullPage = false,
}) {
  const { t } = useTranslation("teamAgenda");
  const theme = useTheme();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const eventColors = useMemo(() => getAgendaEventColors(theme), [theme]);

  const [range, setRange] = useState(() => ({
    from: dayjs().startOf("day").toDate(),
    to: dayjs().endOf("day").toDate(),
  }));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [defaultRange, setDefaultRange] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [videoWarning, setVideoWarning] = useState("");
  const [visibleSources, setVisibleSources] = useState(() => new Set(AGENDA_SOURCE_KEYS));
  const [selectedCalendarIds, setSelectedCalendarIds] = useState(null);

  const { data, isLoading, isError, error } = useUserAgenda({
    from: range.from,
    to: range.to,
  });
  useAgendaGoogleAutoSync();
  const { byAgendaEventId } = useActiveVideoRooms();
  const { createMutation, updateMutation, deleteMutation, createCalendarMutation, updateCalendarMutation, deleteCalendarMutation } = useUserAgendaMutations();

  const events = data?.events || [];
  const calendars = data?.calendars || [];
  const effectiveCalendarIds = useMemo(
    () =>
      selectedCalendarIds ||
      new Set(calendars.map((calendar) => Number(calendar.id)).filter((id) => Number.isFinite(id))),
    [selectedCalendarIds, calendars]
  );
  const defaultCalendarId = useMemo(() => {
    const selected = calendars.filter((calendar) => effectiveCalendarIds.has(Number(calendar.id)));
    return (
      selected.find((calendar) => calendar.canCreate || calendar.canManage) ||
      calendars.find((calendar) => calendar.canCreate || calendar.canManage)
    )?.id;
  }, [calendars, effectiveCalendarIds]);
  const filteredEvents = useMemo(
    () =>
      events
        .filter((ev) =>
          isAgendaEventVisible(ev, {
            visibleSources,
            calendarIds: effectiveCalendarIds,
          })
        )
        .map((ev) => {
          const eventId = Number(ev.meta?.eventId) || parseManualAgendaEventId(ev.id) || null;
          const activeVideo = eventId ? byAgendaEventId.get(eventId) : null;
          return activeVideo ? { ...ev, activeVideo } : ev;
        }),
    [events, visibleSources, effectiveCalendarIds, byAgendaEventId]
  );
  const dayEvents = useMemo(() => {
    const day = dayjs(range.from);
    return filteredEvents
      .filter((ev) => {
        const start = dayjs(String(ev.start || "").replace(" ", "T"));
        if (!start.isValid()) return false;
        if (ev.allDay) return start.isSame(day, "day");
        const end = ev.end ? dayjs(String(ev.end).replace(" ", "T")) : start;
        return start.isBefore(day.endOf("day")) && end.isAfter(day.startOf("day").subtract(1, "ms"));
      })
      .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  }, [filteredEvents, range.from]);
  const headerDate = useMemo(() => {
    const formatted = dayjs(range.from).format("ddd, D [de] MMM");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, [range.from]);
  const agendaPaths = useMemo(() => teamAgendaPaths(basePath), [basePath]);
  const isToday = dayjs(range.from).isSame(dayjs(), "day");

  const handleDatesChange = useCallback(({ start, end }) => {
    setRange({ from: start, to: end });
  }, []);

  const handleToggleSource = useCallback((source) => {
    setVisibleSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  }, []);

  const handleToggleCalendar = useCallback(
    (calendarId) => {
      const id = Number(calendarId);
      if (!Number.isFinite(id)) return;
      setSelectedCalendarIds((prev) => {
        const base = prev || new Set(calendars.map((calendar) => Number(calendar.id)));
        const next = new Set(base);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [calendars]
  );

  const openCreate = useCallback((rangePayload) => {
    setSelectedEvent(null);
    setDefaultRange({ ...rangePayload, calendarId: rangePayload?.calendarId || defaultCalendarId });
    setDialogError("");
    setDialogOpen(true);
  }, [defaultCalendarId]);

  const openScheduleWith = useCallback(
    (person) => {
      const email = String(person?.email || "").trim().toLowerCase();
      if (!email) return;
      const name = String(person?.name || "").trim();
      openCreate({
        ...defaultNewEventRange(),
        title: name ? `Reunião com ${name}` : "",
        attendees: [{ email, ...(name ? { name } : {}) }],
      });
    },
    [openCreate]
  );

  const openDetails = useCallback((ev) => {
    setSelectedEvent(ev);
    setDefaultRange(null);
    setDialogError("");
    setDialogOpen(true);
  }, []);

  const handleEventClick = useCallback(
    (ev) => {
      if (!ev) return;
      if (ev.editable) {
        openDetails(ev);
        return;
      }
      if (ev.source === "next_contact" && ev.meta?.leadId) {
        navigate(chatPath(ev.meta.leadId));
        return;
      }
      if (ev.source === "task" && ev.meta?.boardId) {
        navigate(teamTasksPaths(basePath).board(ev.meta.boardId));
        return;
      }
      openDetails(ev);
    },
    [navigate, basePath, openDetails]
  );

  const handleSave = async (payload, eventId) => {
    setDialogError("");
    setVideoWarning("");
    try {
      const saved = eventId
        ? await updateMutation.mutateAsync({ eventId, payload })
        : await createMutation.mutateAsync(payload);
      setDialogOpen(false);
      const warn = saved?.meta?.videoWarning;
      if (warn?.message) {
        setVideoWarning(t("videoWarningBanner", { message: warn.message }));
      }
    } catch (err) {
      setDialogError(getApiErrorMessage(err, t("saveError")));
    }
  };

  const handleDelete = async (eventId) => {
    const isSeries = Boolean(selectedEvent?.meta?.recurring || selectedEvent?.meta?.recurrenceRule);
    if (!window.confirm(t(isSeries ? "recurrence.deleteSeriesConfirm" : "deleteConfirm"))) return;
    try {
      await deleteMutation.mutateAsync(eventId);
      setDialogOpen(false);
    } catch (err) {
      setDialogError(getApiErrorMessage(err, t("deleteError")));
    }
  };

  const handleStatusChange = async (eventId, status) => {
    setDialogError("");
    try {
      const updated = await updateMutation.mutateAsync({ eventId, payload: { status } });
      setSelectedEvent((prev) => mergeAgendaFeedEvent(prev, updated));
    } catch (err) {
      setDialogError(getApiErrorMessage(err, t("saveError")));
    }
  };

  return (
    <Box
      sx={{
        ...containerStyle,
        width: isMobile ? "100%" : 360,
        minWidth: isMobile ? undefined : 320,
        maxWidth: isMobile ? "100%" : 400,
        display: "flex",
        flexDirection: "column",
        flex: isMobile ? 1 : undefined,
        height: "100%",
        minHeight: 0,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {t("title")}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {t("panelSubtitle")}
          </Typography>
        </Box>
        {!fullPage ? (
          <Tooltip title={t("openFull")}>
            <IconButton size="small" onClick={() => navigate(agendaPaths.root)} aria-label={t("openFull")}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        {onClose ? (
          <IconButton size="small" onClick={onClose} aria-label={t("closePanel")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
      >
        <Button
          size="small"
          variant={isToday ? "contained" : "outlined"}
          onClick={() => calendarRef.current?.today()}
          sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: "0.75rem", lineHeight: 1.5 }}
        >
          {t("today")}
        </Button>
        <Box
          sx={{
            display: "flex",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <IconButton
            size="small"
            onClick={() => calendarRef.current?.prev()}
            aria-label={t("prev")}
            sx={{ borderRadius: 0, p: 0.5 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => calendarRef.current?.next()}
            aria-label={t("next")}
            sx={{ borderRadius: 0, p: 0.5 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ flex: 1, minWidth: 0, textTransform: "capitalize" }}
        >
          {headerDate}
        </Typography>
        <AgendaGoogleSyncButton compact />
        <Tooltip title={t("newEvent")}>
          <IconButton
            size="small"
            color="primary"
            aria-label={t("newEvent")}
            onClick={() => openCreate(defaultNewEventRange())}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <AgendaGoogleConnectPrompt compact />
        <AgendaCalendarFilter
          compact
          calendars={calendars}
          selectedCalendarIds={effectiveCalendarIds}
          onToggleCalendar={handleToggleCalendar}
          onCreateCalendar={(payload) => createCalendarMutation.mutateAsync(payload)}
          onUpdateCalendar={(args) => updateCalendarMutation.mutateAsync(args)}
          onDeleteCalendar={(calendarId) => deleteCalendarMutation.mutateAsync(calendarId)}
          creating={createCalendarMutation.isLoading}
          updating={updateCalendarMutation.isLoading}
          deleting={deleteCalendarMutation.isLoading}
        />
        <AgendaAvailabilityPanel range={range} compact onScheduleWith={openScheduleWith} />
        <AgendaLegend compact selectedSources={visibleSources} onToggleSource={handleToggleSource} />
      </Box>

      {dayEvents.length ? (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
            maxHeight: 120,
            overflowY: "auto",
          }}
        >
          <Stack spacing={0.5}>
            {dayEvents.map((ev) => {
              const start = dayjs(String(ev.start || "").replace(" ", "T"));
              const end = ev.end ? dayjs(String(ev.end).replace(" ", "T")) : null;
              const timeLabel = ev.allDay
                ? t("allDay")
                : end && end.isValid() && end.isAfter(start)
                  ? `${start.format("HH:mm")} – ${end.format("HH:mm")}`
                  : start.format("HH:mm");
              const color = eventColors[ev.source || "manual"] || eventColors.manual;
              return (
                <Box
                  key={String(ev.id)}
                  component="button"
                  type="button"
                  onClick={() => handleEventClick(ev)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderRadius: 1,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    px: 0.5,
                    py: 0.25,
                    appearance: "none",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ flexShrink: 0, minWidth: 64 }}
                  >
                    {timeLabel}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                    {ev.title}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, position: "relative", px: 0.5, pb: 0.5 }}>
        {videoWarning ? (
          <Alert severity="warning" onClose={() => setVideoWarning("")} sx={{ mx: 1, mb: 0.5 }}>
            {videoWarning}
          </Alert>
        ) : null}
        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <CircularProgress size={28} />
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 1 }}>
            {getApiErrorMessage(error, t("loadError"))}
          </Alert>
        ) : (
          <AgendaCalendarCore
            ref={calendarRef}
            events={filteredEvents}
            initialView="timeGridDay"
            height="100%"
            compact
            onDatesChange={handleDatesChange}
            onEventClick={handleEventClick}
            onDateClick={openCreate}
            onEventDrop={(id, p) => updateMutation.mutateAsync({ eventId: id, payload: p })}
            onEventResize={(id, p) => updateMutation.mutateAsync({ eventId: id, payload: p })}
          />
        )}
      </Box>

      <AgendaEventDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialEvent={selectedEvent}
        defaultRange={defaultRange}
        onSave={handleSave}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        saving={createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading}
        error={dialogError}
        calendars={calendars}
        defaultCalendarId={defaultCalendarId}
      />
    </Box>
  );
}
